import { FeatureFlags } from '@/lib/feature-flag';
import { Roles } from '@/features/Users/userTypes';
import { getPreferredClient } from '@/lib/auth';
import type { AnnouncementConfig } from '../types';
import { buildDismissPreferenceKey } from '../keys';

/**
 * Feature flag names for announcements.
 * These must be created in PostHog before use.
 */
export const ANNOUNCEMENT_FLAGS = {
  REPORTING: FeatureFlags.AnnouncementReporting,
} as const;

/**
 * Centralized announcement configurations.
 * Add new announcements here - content can be updated without component changes.
 *
 * Role targeting examples:
 * - targetRoles: [Roles.SuperAdmin] - Only super admins
 * - targetRoles: [Roles.Monitoring, Roles.IntegratorAdmin] - Multiple roles
 * - (omit targetRoles) - All users
 *
 * New user targeting:
 * - existingUsersOnly: true - Only users who existed before onboarding (never shown to new sign-ups)
 * - (omit existingUsersOnly) - All users (new users see it after the 24-hour onboarding delay)
 *
 * Auto-dismiss on visit:
 * - dismissOnVisit: '/reporting' - Auto-dismiss modal & banner when user visits /reporting
 * - Uses startsWith matching, so '/reporting' also matches '/reporting/details'
 */
export const ANNOUNCEMENTS: AnnouncementConfig[] = [
  {
    id: 'reporting-launch',
    featureFlag: ANNOUNCEMENT_FLAGS.REPORTING,
    showModal: true,
    showBanner: false,
    priority: 100,
    existingUsersOnly: true,
    dismissOnVisit: '/reporting',
    targetRoles: [Roles.SuperAdmin, Roles.IntegratorAdmin],
    content: {
      title: 'Introducing Reporting',
      description:
        'Turn monitoring data into clear insights across your clients, sites, and devices.',
      body: `
- **See what automation is really doing** – Understand how Quill is handling events and where human intervention still matters
- **Track activity across your estate** – Compare incidents and trends across customers, sites, and devices
- **Understand time-to-action** – Identify how quickly events are acknowledged and resolved, and where delays occur
- **Break down operational load** – See where attention is spent and which assets drive the most activity
- **Share insights easily** – Export reports as images or CSVs for quick reviews and stakeholder updates
    `.trim(),
      variant: 'feature',
      cta: {
        label: 'Explore Reporting',
        to: '/reporting',
      },
      graphics: [
        '/graphics/reporting-announcement/event-volume.png',
        '/graphics/reporting-announcement/alert-flow.png',
        '/graphics/reporting-announcement/response-time.png',
      ],
      secondaryCta: {
        label: 'Learn More',
        href: 'https://docs.watchful.co.nz/announcements/reporting',
      },
    },
  },
];

/**
 * Get an announcement by its ID
 */
export function getAnnouncementById(id: string): AnnouncementConfig | undefined {
  return ANNOUNCEMENTS.find((a) => a.id === id);
}

/**
 * Get all currently active announcements (within date range if specified)
 */
export function getActiveAnnouncements(): AnnouncementConfig[] {
  const now = new Date();

  return ANNOUNCEMENTS.filter((a) => {
    if (a.startDate && new Date(a.startDate) > now) return false;
    if (a.endDate && new Date(a.endDate) < now) return false;
    return true;
  }).sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
}

/**
 * Get announcements that should display as a specific type
 */
export function getAnnouncementsByDisplayType(type: 'modal' | 'banner'): AnnouncementConfig[] {
  return getActiveAnnouncements().filter((a) => {
    if (type === 'modal') return a.showModal === true;
    if (type === 'banner') return a.showBanner === true;
    return false;
  });
}

/**
 * Dismiss all currently active modal announcements for a user.
 */
export async function dismissAllActiveModalAnnouncements(userId: string): Promise<void> {
  try {
    const activeModalAnnouncements = getAnnouncementsByDisplayType('modal');
    
    // Dismiss each active modal announcement
    const dismissPromises = activeModalAnnouncements.map((announcement) => {
      const key = buildDismissPreferenceKey(announcement.id, 'modal');
      return getPreferredClient().post('/api/UserPreferences', {
        userId,
        key,
        value: 'true',
      });
    });

    await Promise.all(dismissPromises);
  } catch (error) {
    console.error('[Announcements] Failed to dismiss active announcements:', error);
  }
}
